import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { firestore } from '../../../firebase.config';
import { useAuth } from '../../../AuthContext';
import { useUserContext } from '../../../components/context/UserContext';
import {
  Box,
  Card,
  CardHeader,
  CardContent,
  CardActions,
  TextField,
  MenuItem,
  Button,
  Typography,
  Switch,
  FormControlLabel,
  Grid,
  Divider,
  InputAdornment,
  Collapse
} from '@mui/material';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CalculateIcon from '@mui/icons-material/Calculate';
import CancelIcon from '@mui/icons-material/Cancel';
import SaveIcon from '@mui/icons-material/Save';

const TransactionForm = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const { currentUser } = useAuth();
  const { userProfile } = useUserContext();
  const tenantId = userProfile?.tenantId;

  const [entry, setEntry] = useState({
    date: new Date().toISOString().slice(0, 10),
    description: '',
    amount: '',
    bankId: '',
    branchId: '',
    branchName: '',
    accountType: '',
    valueDate: '',
    maturityDate: '',
    depositAmount: '',
    interestRate: '',
    interestType: 'Simple Interest',
    maturityAmount: '',
    borrowerName: '',
    loanAmount: '',
    loanStartDate: '',
    loanEndDate: '',
    emiAmount: '',
    outstandingAmount: '',
    accountNumber: '',
    branchCode: '',
    overdraftLimit: '',
    accountManager: '',
    loanType: ''
  });

  const [banks, setBanks] = useState([]);
  const [bankBranchOptions, setBankBranchOptions] = useState([]);

  const branchCodes = {
    "HDFC Bank": "HDFC0001",
    "ICICI Bank": "ICIC0002",
    "State Bank of India": "SBIN0003",
    "Axis Bank": "UTIB0004",
    "Bank of Baroda": "BARB0005",
    "Punjab National Bank": "PUNB0006",
    "Kotak Mahindra Bank": "KKBK0007",
    "Canara Bank": "CNRB0008",
    "Union Bank": "UBIN0009",
    "IndusInd Bank": "INDB0010"
  };
  const bankBranches = Object.keys(branchCodes);

  // Fetch banks on component mount
  useEffect(() => {
    // Fetch banks and flatten branches for dropdown
    const fetchBanks = async () => {
      const { getDocs, collection } = await import('firebase/firestore');
      const querySnapshot = await getDocs(collection(firestore, 'AccountingBanks'));
      const banksData = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setBanks(banksData);
      // Flatten branches for dropdown: { id, label, bankId, branchIdx, accountNumber }
      const flatBranches = [];
      banksData.forEach(bank => {
        if (Array.isArray(bank.branches)) {
          bank.branches.forEach((branch, idx) => {
            flatBranches.push({
              id: `${bank.id}_${idx}`,
              bankId: bank.id,
              branchIdx: idx,
              label: `${bank.name} (${branch.ifsc})${branch.accountNumber ? ' - ' + branch.accountNumber : ''}`,
              bankName: bank.name,
              ifsc: branch.ifsc,
              branchName: branch.branchName,
              accountNumber: branch.accountNumber || '',
              branchData: branch
            });
          });
        }
      });
      setBankBranchOptions(flatBranches);
    };
    fetchBanks();
  }, []);

  // (Removed fetchBranches and related state)

  const handleChange = (e) => {
    const { name, value } = e.target;
    // If selecting bank+branch+account
    if (name === 'bankBranchId') {
      const selected = bankBranchOptions.find(opt => opt.id === value);
      setEntry(prev => ({
        ...prev,
        bankId: selected?.bankId || '',
        branchId: selected?.branchIdx?.toString() || '',
        branchName: selected?.branchName || '',
        branchCode: selected?.ifsc || '',
        accountNumber: selected?.accountNumber || ''
      }));
    } else {
      setEntry(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSave = async () => {
    if (!tenantId) {
      alert('Tenant not found. Please log in again.');
      return;
    }
    if (!entry.date || !entry.description || !entry.amount) {
      alert('Please fill all required fields');
      return;
    }
    if (isNaN(parseFloat(entry.amount)) || parseFloat(entry.amount) <= 0) {
      alert('Please enter a valid amount');
      return;
    }
    setLoading(true);
    try {
      const amountValue = parseFloat(entry.amount).toFixed(2);
      // Only include fields that are not empty, null, or undefined
      const filtered = Object.fromEntries(
        Object.entries({
          ...entry,
          amount: amountValue,
          createdBy: currentUser?.uid || currentUser?.email || '',
          createdAt: serverTimestamp(),
          tenantId: tenantId
        }).filter(([_, v]) => v !== '' && v !== null && v !== undefined)
      );
      await addDoc(collection(firestore, 'Accounting Transactions Master'), filtered);
      setLoading(false);
      navigate(-1);
    } catch (err) {
      setLoading(false);
      alert('Failed to save transaction: ' + (err.message || err));
    }
  };

  // simple auto-calc for maturity amount when deposit fields change
  useEffect(() => {
    if (entry.depositAmount && entry.interestRate && entry.valueDate && entry.maturityDate) {
      try {
        const principal = parseFloat(entry.depositAmount) || 0;
        const rate = parseFloat(entry.interestRate) / 100 || 0;
        const start = new Date(entry.valueDate);
        const end = new Date(entry.maturityDate);
        const years = Math.max(0, (end - start) / (1000 * 60 * 60 * 24 * 365));
        let maturity = principal;
        if (entry.interestType === 'Simple Interest') {
          maturity = principal + principal * rate * years;
        } else {
          maturity = principal * Math.pow(1 + rate, years);
        }
        if (!Number.isNaN(maturity)) setEntry(prev => ({ ...prev, maturityAmount: maturity.toFixed(2) }));
      } catch (err) {
        // ignore
      }
    }
  }, [entry.depositAmount, entry.interestRate, entry.valueDate, entry.maturityDate, entry.interestType]);

  return (
    <Box sx={{ p: 3 }}>
      <Card sx={{ borderRadius: 2, boxShadow: 3 }}>
        <CardHeader
          title={<Typography variant="h6">Add Transaction</Typography>}
          action={
            <FormControlLabel
              control={
                <Switch
                  checked={entry.type === 'Credit'}
                  onChange={(e) => setEntry(prev => ({ ...prev, type: e.target.checked ? 'Credit' : 'Debit' }))}
                  color="primary"
                />
              }
              label={entry.type === 'Credit' ? 'Credit' : 'Debit'}
            />
          }
          sx={{ pb: 0 }}
        />
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={2}>
              <TextField
                label="Date"
                type="date"
                name="date"
                value={entry.date}
                onChange={handleChange}
                size="small"
                fullWidth
                InputLabelProps={{ shrink: true }}
                InputProps={{ startAdornment: (
                  <InputAdornment position="start"><CalendarTodayIcon fontSize="small" /></InputAdornment>
                ) }}
              />
            </Grid>
            <Grid item xs={12} md={7}>
              <TextField
                label="Description"
                name="description"
                value={entry.description}
                onChange={handleChange}
                size="small"
                fullWidth
                multiline
                minRows={1}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                label="Amount"
                name="amount"
                type="number"
                value={entry.amount}
                onChange={handleChange}
                size="small"
                fullWidth
                InputProps={{ startAdornment: (
                  <InputAdornment position="start"><MonetizationOnIcon fontSize="small" /></InputAdornment>
                ) }}
              />
            </Grid>


            <Grid item xs={12} md={6}>
              <TextField
                select
                name="bankBranchId"
                value={bankBranchOptions.find(opt => opt.bankId === entry.bankId && opt.accountNumber === entry.accountNumber)?.id || ''}
                onChange={handleChange}
                size="small"
                fullWidth
                label="Select Bank, IFSC & Account"
                SelectProps={{ displayEmpty: true }}
                InputProps={{ startAdornment: (
                  <InputAdornment position="start"><AccountBalanceIcon fontSize="small" /></InputAdornment>
                ) }}
              >
                <MenuItem value=""><em>Select Bank, IFSC & Account</em></MenuItem>
                {bankBranchOptions.map(opt => (
                  <MenuItem key={opt.id} value={opt.id}>{opt.label}</MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                select
                label="Account Type"
                name="accountType"
                value={entry.accountType}
                onChange={handleChange}
                size="small"
                fullWidth
              >
                <MenuItem value="Current">Current</MenuItem>
                <MenuItem value="Saving">Saving</MenuItem>
                <MenuItem value="Deposit">Fixed Deposit</MenuItem>
                <MenuItem value="Loan">Loan</MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                Details
              </Typography>
            </Grid>

          </Grid>

          <Box sx={{ mt: 2 }}>
            {/* Fixed Deposit Section */}
            <Collapse in={entry.accountType === 'Deposit'}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={3}>
                  <TextField
                    label="Value Date"
                    name="valueDate"
                    type="date"
                    value={entry.valueDate}
                    onChange={handleChange}
                    size="small"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    InputProps={{ startAdornment: (<InputAdornment position="start"><CalendarTodayIcon fontSize="small" /></InputAdornment>) }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    label="Maturity Date"
                    name="maturityDate"
                    type="date"
                    value={entry.maturityDate}
                    onChange={handleChange}
                    size="small"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    label="Principal Amount"
                    name="depositAmount"
                    type="number"
                    value={entry.depositAmount}
                    onChange={handleChange}
                    size="small"
                    fullWidth
                    InputProps={{ startAdornment: (<InputAdornment position="start"><MonetizationOnIcon fontSize="small" /></InputAdornment>) }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    label="Interest Rate (%)"
                    name="interestRate"
                    type="number"
                    value={entry.interestRate}
                    onChange={handleChange}
                    size="small"
                    fullWidth
                    InputProps={{ endAdornment: (<InputAdornment position="end">%</InputAdornment>) }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    select
                    label="Interest Type"
                    name="interestType"
                    value={entry.interestType}
                    onChange={handleChange}
                    size="small"
                    fullWidth
                  >
                    <MenuItem value="Simple Interest">Simple Interest</MenuItem>
                    <MenuItem value="Cumulative Interest">Cumulative Interest</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Maturity Amount"
                    name="maturityAmount"
                    type="number"
                    value={entry.maturityAmount}
                    onChange={handleChange}
                    size="small"
                    fullWidth
                    helperText="Calculated from principal, rate and duration (editable)"
                    InputProps={{ startAdornment: (<InputAdornment position="start"><CalculateIcon fontSize="small" /></InputAdornment>) }}
                  />
                </Grid>
              </Grid>
            </Collapse>

            {/* Loan Section */}
            <Collapse in={entry.accountType === 'Loan'}>
              <Grid container spacing={2} alignItems="center" sx={{ mt: 1 }}>
             
                <Grid item xs={12} md={3}>
                  <TextField
                    label="Loan Start Date"
                    name="loanStartDate"
                    type="date"
                    value={entry.loanStartDate}
                    onChange={handleChange}
                    size="small"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    label="Loan End Date"
                    name="loanEndDate"
                    type="date"
                    value={entry.loanEndDate}
                    onChange={handleChange}
                    size="small"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    label="EMI Amount"
                    name="emiAmount"
                    type="number"
                    value={entry.emiAmount}
                    onChange={handleChange}
                    size="small"
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    label="Outstanding Amount"
                    name="outstandingAmount"
                    type="number"
                    value={entry.outstandingAmount}
                    onChange={handleChange}
                    size="small"
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                <TextField
                select
                name="loanType"
                value={entry.loanType}
                onChange={handleChange}
                size="small"
                fullWidth
                label="Loan Type"
              >
                <MenuItem value="Personal">Personal</MenuItem>
                <MenuItem value="Home">Home</MenuItem>
                <MenuItem value="Vehicle">Vehicle</MenuItem>
                <MenuItem value="Education">Education</MenuItem>
                <MenuItem value="Business">Business</MenuItem>
              </TextField>
              </Grid>
              </Grid>
            </Collapse>
          </Box>
        </CardContent>
        <CardActions sx={{ justifyContent: 'flex-end', px: 3, py: 2 }}>
          <Button
            variant="outlined"
            startIcon={<CancelIcon />}
            onClick={() => navigate(-1)}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={loading}
          >
            {loading ? 'Saving...' : 'Save'}
          </Button>
        </CardActions>
      </Card>
    </Box>
  );
};

export default TransactionForm;
